import { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { isValidPhoneNumber, isPossiblePhoneNumber } from 'libphonenumber-js';

import usePortalSettings from '../../hooks/usePortalSettings';
import DataContext from '../../store/dataContext';
import useApiPostOrderError from '../../hooks/useApiPostOrderError';
import useErrors from '../../store/errors/useErrors';

const useBillingPhone = () => {
  const { portalSettings } = usePortalSettings();
  const dataContext = useContext(DataContext);
  const { hasPhoneError } = useApiPostOrderError();
  const { errors, addErrorToField, hasErrorOnField } = useErrors();
  const { t } = useTranslation();

  const { billingPhone } = dataContext;

  const isBillingPhoneOk = ({ shouldShowError } = { shouldShowError: true }) => {
    const hasPhoneRequired = portalSettings.contactPhone.isRequired || dataContext.consent?.hasSms;
    const hasPhoneRequiredAndValueIsEmpty =
      (portalSettings.contactPhone.isRequired && !billingPhone) || (dataContext.consent?.hasSms && !billingPhone);

    const ifValidPhoneFromContext = context => {
      let result = false;
      if (billingPhone && context && hasPhoneRequiredAndValueIsEmpty) {
        result = !isValidPhoneNumber(billingPhone, context);
      }
      return result;
    };

    const phoneHasError = () =>
      // ApiPostOrderError and phone was not typed
      (hasPhoneError() && !billingPhone) ||
      // Phone is Required and there is no phone value
      hasPhoneRequiredAndValueIsEmpty ||
      // Local phone error and phone input field is visible
      (hasErrorOnField('billingPhone') && portalSettings.contactPhone.isVisible) ||
      // Phone value and contextBillingAddress
      ifValidPhoneFromContext(dataContext?.contextBillingAddress?.countryIso2) ||
      // Phone value and contextShippingAddress
      ifValidPhoneFromContext(dataContext?.contextShippingAddress?.countryIso2) ||
      // Not a possible phone number
      (hasPhoneRequired && !isPossiblePhoneNumber(`+${billingPhone}`));

    if (phoneHasError()) {
      if (shouldShowError) {
        addErrorToField({
          billingPhone: {
            hasHighlight: false,
            message: errors.billingPhone.message || t('payment_add.please_enter_valid_phone_number'),
          },
        });
      }
      return false;
    }
    return true;
  };

  return { isBillingPhoneOk };
};

export default useBillingPhone;
